import React from 'react';
import PropTypes from 'prop-types';

import CustomerDistributionListsModal from './CustomerDistributionListsModal';
import PhoneInputComp from '../Input/PhoneInputComp';
import Input from '../Input/Input';
import { Field, reduxForm } from 'redux-form';
import { ENV, ENVIRONMENTS } from "../../constants/EndPoints";

class CustomerDetails extends React.Component {
  constructor(props) {
    super(props);

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleLogoUpload = this.handleLogoUpload.bind(this);
    this.fetchLogoUrl = this.fetchLogoUrl.bind(this);

    this.state = {
      distributionListManageOpen: false,
      uploadingLogo: false,
      logoPreview: null,
      logoUrl: null,
    };
  }

  componentDidMount() {
    this.fetchLogoUrl();
  }

  componentDidUpdate(prevProps) {
    const prevCustomerId = prevProps.selectedCustomerId ?? prevProps.initialValues?.ID;
    const nextCustomerId = this.props.selectedCustomerId ?? this.props.initialValues?.ID;

    const prevLogoKey = `${prevProps.initialValues?.S3Bucket || ''}/${prevProps.initialValues?.Logo || ''}`;
    const nextLogoKey = `${this.props.initialValues?.S3Bucket || ''}/${this.props.initialValues?.Logo || ''}`;

    if (prevCustomerId !== nextCustomerId || prevLogoKey !== nextLogoKey) {
      this.setState({ logoUrl: null, logoPreview: null });
      this.fetchLogoUrl();
    }
  }

  fetchLogoUrl() {
    const customerId = this.props.selectedCustomerId ?? this.props.initialValues?.ID;

    if (customerId && this.props.fetchLogoUrl) {
      this.activeLogoCustomerId = customerId;
      this.props.fetchLogoUrl(this.props.initialValues, (result) => {
        if (this.activeLogoCustomerId !== customerId) return;
        if (result.status && result.data) {
          const signedUrl = result.data;

          fetch(signedUrl)
            .then(response => {
              if (response.ok) {
                return response.text();
              }
              throw new Error('Failed to fetch image from S3');
            })
            .then(base64 => {
              if (this.activeLogoCustomerId !== customerId) return;
              this.setState({ logoUrl: base64 });
            })
            .catch(error => {
              console.error('Error fetching logo image:', error);
            });
        }
      });
    }
  }

  handleLogoUpload(event) {
    const file = event.target.files[0];
    if (file) {
      // Validate file is an image
      if (!file.type.startsWith('image/')) {
        this.props.notify('Please select an image file', 'error');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.props.notify('Image size must be less than 5MB', 'error');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        this.setState({ logoPreview: reader.result });

        // Upload the logo
        this.setState({ uploadingLogo: true });
        this.props.uploadLogo(reader.result, (result) => {
          this.setState({ uploadingLogo: false });
          if (result.status) {
            this.props.notify('Logo uploaded successfully', 'success');
            // Refresh logo URL after successful upload
            this.fetchLogoUrl();
          } else {
            this.props.notify('Failed to upload logo', 'error');
            this.setState({ logoPreview: null });
          }
        });
      };
      reader.readAsDataURL(file);
    }
  }

  openModal() {
    this.setState({
      distributionListManageOpen: true,
    });
  }

  closeModal() {
    this.setState({
      distributionListManageOpen: false,
    });
  }

  render() {
    const { handleSubmit, user, pristine, submitting, isNew, userEditMode, initialValues } =
      this.props;
    const labelClassName = 'col-xs-4 control-label';
    const controlClassName = 'col-xs-6 control-input';
    const buttonTitle = isNew
      ? this.context.t('Create New Customer')
      : this.context.t('update_customer_details');

    // Get logo URL - prefer preview (while uploading), then fetched signed URL
    const logoUrl = this.state.logoPreview || this.state.logoUrl;

    return (
      <div>
        <form
          className='form-horizontal sticky-actions'
          onSubmit={handleSubmit}
        >
          <div className='form-group'>
            <label className={labelClassName}>
              {this.context.t('customer_name')}:
            </label>
            <div className={controlClassName}>
              <Field
                name='Name'
                component={Input}
                type='text'
                className='form-control'
                disabled={!isNew}
              />
            </div>
          </div>

          {!isNew && (
            <div className='form-group'>
              <label className={labelClassName}>
                {this.context.t('customer_logo')}:
              </label>
              <div className={controlClassName}>
                <div style={{ marginBottom: '10px' }}>
                  {logoUrl && (
                    <img
                      src={logoUrl}
                      alt="Customer Logo"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '100px',
                        display: 'block',
                        marginBottom: '10px',
                        border: '1px solid #ddd',
                        padding: '5px'
                      }}
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={this.handleLogoUpload}
                    disabled={this.state.uploadingLogo}
                    style={{ display: 'block' }}
                  />
                  {this.state.uploadingLogo && (
                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                      Uploading...
                    </small>
                  )}
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Supported formats: JPG, PNG, GIF (Max 5MB)
                  </small>
                </div>
              </div>
            </div>
          )}

          <div className='form-group'>
            <label className={labelClassName}>
              {this.context.t('contact_person')}:
            </label>
            <div className={controlClassName}>
              <Field
                name='ContactPerson'
                component={Input}
                type='text'
                className='form-control'
              />
            </div>
          </div>

          <div className='form-group'>
            <label className={labelClassName}>
              {this.context.t('contact_email')}:
            </label>
            <div className={controlClassName}>
              <Field
                name='ContactMail'
                component={Input}
                type='email'
                className='form-control'
              />
            </div>
          </div>

          <div className='form-group'>
            <label className={labelClassName}>
              {this.context.t('contact_phone_1')}:
            </label>
            <div className={controlClassName}>
              <Field
                name='ContactPhone1'
                component={PhoneInputComp}
                type='tel'
                className='tel-input'
              />
            </div>
          </div>

          <div className='form-group'>
            <label className={labelClassName}>
              {this.context.t('contact_phone_2')}:
            </label>
            <div className={controlClassName}>
              <Field
                name='ContactPhone2'
                component={PhoneInputComp}
                type='tel'
                className='tel-input'
              />
            </div>
          </div>

          <div className='form-actions'>
            <button
              className='btn btn-success btn-submit'
              disabled={pristine || submitting}
              type='submit'
            >
              {buttonTitle}
            </button>

            <a
              style={{
                float: 'left',
                pointerEvents: !user.isAQS ? 'none' : 'auto',
                opacity: !user.isAQS ? 0.5 : 1,
              }}
              className='btn btn-info btn-submit'
              onClick={!user.isAQS ? null : this.openModal}
            >
              {this.context.t('manage_distribution_lists')}
            </a>

            {JSON.parse(process.env.REACT_APP_FILES) && user.isAQS && (
              <a
                style={{ float: 'left', marginLeft: '10px' }}
                className='btn btn-info'
                onClick={this.props.configureCustomerBucket}
                disabled={!userEditMode}
              >
                {this.context.t('configure_bucket')}
              </a>
            )}
          </div>
        </form>

        <CustomerDistributionListsModal
          open={this.state.distributionListManageOpen}
          closeFunction={this.closeModal}
          notify={this.props.notify}
          isProjectLevel={false}
        />
      </div>
    );
  }
}

const validate = (values, props) => {
  const errors = {};
  const requireFields = ['Name', 'ContactPerson', 'ContactMail', 'ContactPhone1'];

  requireFields.forEach((key) => {
    if (values[key] == null) {
      errors[key] = 'Can\'t be empty';
    }
  });

  return errors;
};

CustomerDetails.contextTypes = {
  t: PropTypes.func.isRequired
};

const customerDetails = reduxForm({
  form: 'customerDetails', // a unique name for this form
  enableReinitialize: true,
  validate,
})(CustomerDetails);

export default customerDetails;
